import { usePostTemperatureLedgerApi, usePutTemperatureLedgerApi } from 'api/temperature_ledger'
import { Flex } from 'components/shared/flex'
import { CheckboxField, DateField, InputField, TimePickerField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Accordion, Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { InspectTemperatureLedgerRow, TemperatureLedgerMaster, TemperatureLedgerForm, MonthlyTemperatureLedger } from 'entities/temperature_ledger'
import { useFetchEquipmentsApi } from 'api/equipment'
import { CircleButton } from 'components/shared/widget'
import { DayOfWeekStrJP, getDayOfWeekJp } from 'utils/util'
import { isHoliday } from '@holiday-jp/holiday_jp'
import moment from 'moment'

const dayColor = (year: number, month: number, day: number) => {
    const date = new Date(year, month - 1, day)
    const dayOfWeek: DayOfWeekStrJP = getDayOfWeekJp(date)
    if (dayOfWeek === '土') {
        return 'rgb(107 125 235 / 100%)'
    } else if (dayOfWeek === '日' || isHoliday(date)) {
        return 'rgb(233 100 119 / 100%)'
    } else {
        return '#000'
    }
}

const FormView = (form: Form<TemperatureLedgerMaster>, apiError: ApiError, isNew?: boolean) => {
    const equipmentApi = useFetchEquipmentsApi()
    useEffect(() => {
        equipmentApi.execute()
    }, [])
    return (
        <Flex flexDirection="column">
            <Accordion style={{ width: '100%' }}>
                <Accordion.Item eventKey="monthly">
                    <Accordion.Header>月次情報</Accordion.Header>
                    <Accordion.Body>
                        <Flex>
                            <InputField required apiError={apiError} form={form} attr="controlTemperatureMax" type="number" label="管理温度の最大値(℃)" />
                            <InputField required apiError={apiError} form={form} attr="controlTemperatureMin" type="number" label="管理温度の最小値(℃)" />
                        </Flex>
                        <Flex>
                            <InputField apiError={apiError} form={form} attr="controlHumidityMax" type="number" label="管理湿度の最大値(%)" />
                            <InputField apiError={apiError} form={form} attr="controlHumidityMin" type="number" label="管理湿度の最小値(%)" />
                        </Flex>
                        <Flex>
                            <InputField type="number" form={form} attr="proofreading" label="校正(℃)" />
                        </Flex>
                    </Accordion.Body>
                </Accordion.Item>
                {!isNew && (
                    <>
                        <Accordion.Item eventKey="daily">
                            <Accordion.Header>日次情報</Accordion.Header>
                            <Accordion.Body style={{ padding: 0 }}>
                                <Table className="sticky-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>日時</th>
                                            <th>点検OK</th>
                                            <th>測定開始・終了時間</th>
                                            <th>測定温度(℃)</th>
                                            <th>最高温度(℃)</th>
                                            <th>最低温度(℃)</th>
                                            {!form.object.equipment?.ondotoriSerialNumber ||
                                                (form.object.equipment?.ondotoriSeries === 'TR7' && (
                                                    <>
                                                        <th>測定湿度(%)</th>
                                                        <th>最高湿度(%)</th>
                                                        <th>最低湿度(%)</th>
                                                    </>
                                                ))}
                                            <th>特記事項</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array(new Date(Number(form.object.year ?? 0), Number(form.object.month ?? 0), 0).getDate())
                                            .fill(0)
                                            .map((_, index) => {
                                                const temperatureLedgerToday = form.object.monthlyTemperatureLedgers?.find(
                                                    (mtl) => mtl && mtl.day === index + 1
                                                )
                                                const overTempMax =
                                                    temperatureLedgerToday &&
                                                    !!temperatureLedgerToday?.temperatureMax &&
                                                    !!form.object.controlTemperatureMax &&
                                                    temperatureLedgerToday.temperatureMax > form.object.controlTemperatureMax

                                                const overTempMin =
                                                    temperatureLedgerToday &&
                                                    !!temperatureLedgerToday?.temperatureMin &&
                                                    !!form.object.controlTemperatureMin &&
                                                    temperatureLedgerToday.temperatureMin < form.object.controlTemperatureMin

                                                const overHumidityMax =
                                                    temperatureLedgerToday &&
                                                    !!temperatureLedgerToday?.humidityMax &&
                                                    !!form.object?.controlHumidityMax &&
                                                    temperatureLedgerToday.humidityMax > form.object.controlHumidityMax

                                                const overHumidityMin =
                                                    temperatureLedgerToday &&
                                                    !!temperatureLedgerToday?.humidityMin &&
                                                    !!form.object?.controlHumidityMin &&
                                                    temperatureLedgerToday.humidityMin < form.object.controlHumidityMin

                                                const isManageByOndotori = !!form.object.equipment?.ondotoriSerialNumber
                                                const disabled = isManageByOndotori || !form.getValue(['monthlyTemperatureLedgers', index, 'isChecked'])
                                                return (
                                                    <tr key={index}>
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            <div
                                                                style={{
                                                                    color: dayColor(Number(form.object.year ?? 0), Number(form.object.month ?? 0), index + 1),
                                                                }}
                                                            >
                                                                {index + 1}日 (
                                                                {getDayOfWeekJp(
                                                                    new Date(Number(form.object.year ?? 0), Number(form.object.month ?? 0) - 1, index + 1)
                                                                )}
                                                                )
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <Flex justifyContent="center" alignItems="center" style={{ height: 40 }}>
                                                                {isManageByOndotori && form.getValue(['monthlyTemperatureLedgers', index, 'user']) ? (
                                                                    <CheckboxField
                                                                        form={form}
                                                                        attr={['monthlyTemperatureLedgers', index, 'userId']}
                                                                        checkboxLabel="OK"
                                                                        disabled
                                                                    />
                                                                ) : (
                                                                    <CheckboxField
                                                                        form={form}
                                                                        attr={['monthlyTemperatureLedgers', index, 'isChecked']}
                                                                        checkboxLabel="OK"
                                                                        onChange={(e) => {
                                                                            form.update((f) => {
                                                                                let mtl = (f.monthlyTemperatureLedgers ?? [])[index]
                                                                                if (mtl) {
                                                                                    mtl.isChecked = e?.target.checked
                                                                                    mtl.day = index + 1
                                                                                } else {
                                                                                    mtl = { isChecked: e?.target.checked, day: index + 1 }
                                                                                }
                                                                                f.monthlyTemperatureLedgers![index] = mtl
                                                                            })
                                                                        }}
                                                                    />
                                                                )}
                                                            </Flex>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <TimePickerField
                                                                    form={form}
                                                                    attr={['monthlyTemperatureLedgers', index, 'time']}
                                                                    defaultValue={
                                                                        (form.object.monthlyTemperatureLedgers ?? [])[index]?.time
                                                                            ? moment(
                                                                                  `${form.object.year}-${form.object.month}-${index + 1} ${
                                                                                      form.object.monthlyTemperatureLedgers![index].time
                                                                                  }`
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    disabled={disabled}
                                                                    style={{ marginBottom: 0 }}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <InputField
                                                                type="number"
                                                                min={-20}
                                                                max={50}
                                                                form={form}
                                                                disabled={disabled}
                                                                attr={['monthlyTemperatureLedgers', index, 'temperatureAtMeasurement']}
                                                                style={{ marginBottom: 0 }}
                                                                step={0.1}
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputField
                                                                type="number"
                                                                min={-20}
                                                                max={50}
                                                                form={form}
                                                                disabled={disabled}
                                                                attr={['monthlyTemperatureLedgers', index, 'temperatureMax']}
                                                                style={{ marginBottom: 0 }}
                                                                isInvalid={!temperatureLedgerToday?.memo && overTempMax}
                                                                step={0.1}
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputField
                                                                type="number"
                                                                min={-20}
                                                                max={50}
                                                                form={form}
                                                                disabled={disabled}
                                                                attr={['monthlyTemperatureLedgers', index, 'temperatureMin']}
                                                                style={{ marginBottom: 0 }}
                                                                isInvalid={!temperatureLedgerToday?.memo && overTempMin}
                                                                step={0.1}
                                                            />
                                                        </td>
                                                        {!form.object.equipment?.ondotoriSerialNumber ||
                                                            (form.object.equipment?.ondotoriSeries === 'TR7' && (
                                                                <>
                                                                    <td>
                                                                        <InputField
                                                                            type="number"
                                                                            min={0}
                                                                            max={100}
                                                                            form={form}
                                                                            disabled={disabled}
                                                                            attr={['monthlyTemperatureLedgers', index, 'humidityAtMeasurement']}
                                                                            style={{ marginBottom: 0 }}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <InputField
                                                                            type="number"
                                                                            min={0}
                                                                            max={100}
                                                                            form={form}
                                                                            disabled={disabled}
                                                                            attr={['monthlyTemperatureLedgers', index, 'humidityMax']}
                                                                            style={{ marginBottom: 0 }}
                                                                            isInvalid={!temperatureLedgerToday?.memo && overHumidityMax}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <InputField
                                                                            type="number"
                                                                            min={0}
                                                                            max={100}
                                                                            form={form}
                                                                            disabled={disabled}
                                                                            attr={['monthlyTemperatureLedgers', index, 'humidityMin']}
                                                                            isInvalid={!temperatureLedgerToday?.memo && overHumidityMin}
                                                                            style={{ marginBottom: 0 }}
                                                                        />
                                                                    </td>
                                                                </>
                                                            ))}
                                                        <td>
                                                            <InputField
                                                                form={form}
                                                                attr={['monthlyTemperatureLedgers', index, 'memo']}
                                                                style={{ marginBottom: 0 }}
                                                                isInvalid={
                                                                    !!form.getValue(['monthlyTemperatureLedgers', index, 'temperatureAtMeasurement']) &&
                                                                    !form.getValue(['monthlyTemperatureLedgers', index, 'memo']) &&
                                                                    (overTempMax || overTempMin || overHumidityMax || overHumidityMin)
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="inspect">
                            <Accordion.Header>点検情報</Accordion.Header>
                            <Accordion.Body>
                                <InputField form={form} attr="maintenanceCompany" label="保守点検業者名" />
                                <table style={{ width: '100%' }}>
                                    <tr>
                                        <th></th>
                                        <th style={{ width: 150 }}>点検日</th>
                                        <th style={{ width: 600 }}>作業内容</th>
                                        <th style={{ width: 500 }}>申し送り事項</th>
                                    </tr>
                                    <tbody>
                                        {form.object.inspectTemperatureLedgers?.map((_, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div style={{ marginBottom: 22 }}>
                                                        <CircleButton
                                                            onClick={() => {
                                                                form.update((f) => f.inspectTemperatureLedgers?.splice(i, 1))
                                                            }}
                                                            variant="danger"
                                                        >
                                                            -
                                                        </CircleButton>
                                                    </div>
                                                </td>
                                                <td>
                                                    <DateField
                                                        minDate={new Date(`${form.object.year}-${form.object.month}-1`)}
                                                        maxDate={new Date(Number(form.object.year), Number(form.object.month), 0)}
                                                        form={form}
                                                        attr={['inspectTemperatureLedgers', i, 'date']}
                                                    />
                                                </td>
                                                <td>
                                                    <InputField form={form} attr={['inspectTemperatureLedgers', i, 'content']} />
                                                </td>
                                                <td>
                                                    <InputField form={form} attr={['inspectTemperatureLedgers', i, 'message']} />
                                                </td>
                                            </tr>
                                        ))}
                                        <td>
                                            <CircleButton
                                                onClick={() => {
                                                    form.update((f) => f.inspectTemperatureLedgers?.push({ temperatureLedgerMasterId: form.object.id }))
                                                }}
                                                variant="primary"
                                            >
                                                +
                                            </CircleButton>
                                        </td>
                                    </tbody>
                                </table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </>
                )}
            </Accordion>
        </Flex>
    )
}

type NewTemperatureLedgerFormProps = {
    onComplete?: () => void
    temperatureLedgerForm?: Form<TemperatureLedgerForm>
    disabled?: boolean
}

export const NewTemperatureLedgerForm = (props: NewTemperatureLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostTemperatureLedgerApi()
    const form = useForm<TemperatureLedgerForm>({
        monthlyTemperatureLedgers: Array(
            new Date(Number(props.temperatureLedgerForm?.object.year ?? 0), Number(props.temperatureLedgerForm?.object.month ?? 0), 0).getDate()
        )
            .fill(0)
            .map((_, i) => ({ day: i + 1 })),
        inspectTemperatureLedgers: Array(InspectTemperatureLedgerRow)
            .fill(0)
            .map((_, i) => ({})),
    })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffect(() => {
        form.update((f) => {
            f.year = props.temperatureLedgerForm?.object.year
            f.month = props.temperatureLedgerForm?.object.month
            f.equipmentId = props.temperatureLedgerForm?.object.equipmentId
        })
    }, [props.temperatureLedgerForm?.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                disabled={props.disabled}
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                当月の台帳を登録
            </Button>
            <Modal fullscreen show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>温度設備管理台帳の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h3 style={{ marginBottom: 10 }}>{`${form.object.year}年${('0' + form.object.month).slice(-2)}月 / ${form.object.equipment?.name}`}</h3>
                    <>{FormView(form, api.apiError, true)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditTemperatureLedgerFormProps = {
    temperatureLedger: TemperatureLedgerMaster
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditTemperatureLedgerForm = (props: EditTemperatureLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutTemperatureLedgerApi()
    const form = useForm<TemperatureLedgerForm>({ ...props.temperatureLedger })

    useEffect(() => {
        const mtls: MonthlyTemperatureLedger[] = []
        props.temperatureLedger.monthlyTemperatureLedgers?.forEach((mtl, index) => {
            if (!mtl.day) {
                return
            }
            mtls[mtl.day - 1] = { ...mtl, isChecked: !!mtl.userId }
        })
        form.set({
            ...props.temperatureLedger,
            monthlyTemperatureLedgers: mtls,
            inspectTemperatureLedgers: props.temperatureLedger.inspectTemperatureLedgers,
        })
    }, [props.temperatureLedger])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal fullscreen show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>温度設備管理台帳の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '0 20px' }}>
                {form.object.id && (
                    <>
                        <h3 style={{ marginBottom: 10, marginTop: 10 }}>{`${form.object.year}年${('0' + form.object.month).slice(-2)}月 / ${
                            form.object.equipment?.name
                        }`}</h3>
                        {FormView(form, api.apiError)}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
